import { useEffect, useRef, useState, type FormEvent } from 'react'
import { authApi, describeError } from '@/lib/auth'
import { useAuthStore } from '@/store/auth'
import { DesktopTitlebar } from '@/components/DesktopTitlebar'
import { Field } from '@/pages/AuthPage'
import styles from './AuthPage.module.css'

export interface EmailAction {
  kind: 'verify' | 'recover'
  token: string
}

/**
 * Страницы по ссылкам из писем. Работают и без входа: письмо могли открыть на другом
 * устройстве. Подтверждение адреса — сразу, без кнопки: ссылка одноразовая, второй раз
 * её всё равно не открыть. Смена пароля — форма с новым паролем, после неё сразу вход.
 */
export function EmailActionPage({ action, onDone }: { action: EmailAction; onDone: () => void }) {
  return (
    <div className={styles.page}>
      <DesktopTitlebar />
      <div className={styles.card}>
        <img className={styles.brand} src="/brand/mark.png" alt="Кличат" width={56} height={56} />
        {action.kind === 'verify' ? <VerifyEmail token={action.token} onDone={onDone} /> : <RecoverByLink token={action.token} onDone={onDone} />}
      </div>
    </div>
  )
}

type VerifyState = { kind: 'busy' } | { kind: 'ok'; email: string } | { kind: 'error'; message: string }

function VerifyEmail({ token, onDone }: { token: string; onDone: () => void }) {
  const [state, setState] = useState<VerifyState>({ kind: 'busy' })
  // ссылка одноразовая: повторный запрос (StrictMode, смена статуса входа) её только сожжёт
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return
    started.current = true
    void (async () => {
      try {
        const res = await authApi.emailVerify(token)
        // вошедшему обновляем профиль, чтобы в настройках сразу было «подтверждена»
        if (useAuthStore.getState().status === 'authenticated') {
          try {
            useAuthStore.getState().setUser(await authApi.me())
          } catch {
            /* обновится при следующем входе */
          }
        }
        setState({ kind: 'ok', email: res.email })
      } catch (err) {
        setState({ kind: 'error', message: describeError(err).message })
      }
    })()
  }, [token])

  if (state.kind === 'busy') {
    return (
      <div className={styles.form}>
        <h1 className={styles.title}>Подтверждаем почту…</h1>
        <p className={styles.muted} aria-live="polite">Секунду.</p>
      </div>
    )
  }
  if (state.kind === 'error') {
    return (
      <div className={styles.form}>
        <h1 className={styles.title}>Не получилось</h1>
        <p className={styles.subtitle}>{state.message}</p>
        <p className={styles.muted}>Новую ссылку можно запросить в настройках учётной записи, раздел «Моя учётная запись».</p>
        <button type="button" className={styles.primary} onClick={onDone}>В Кличат</button>
      </div>
    )
  }
  return (
    <div className={styles.form}>
      <h1 className={styles.title}>Почта подтверждена</h1>
      <p className={styles.subtitle}>
        {state.email} теперь привязана к вашей учётной записи. Через неё можно восстановить пароль, если смените номер,
        а о входе с нового устройства придёт письмо.
      </p>
      <button type="button" className={styles.primary} onClick={onDone}>В Кличат</button>
    </div>
  )
}

function RecoverByLink({ token, onDone }: { token: string; onDone: () => void }) {
  const setSession = useAuthStore((s) => s.setSession)
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fields, setFields] = useState<Record<string, string>>({})
  const [done, setDone] = useState(false)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    setFields({})
    try {
      setSession(await authApi.recoverByEmailComplete({ token, new_password: password }))
      setDone(true)
    } catch (err) {
      const d = describeError(err)
      setError(d.message)
      setFields(d.fields)
    } finally {
      setBusy(false)
    }
  }

  if (done) {
    return (
      <div className={styles.form}>
        <h1 className={styles.title}>Пароль изменён</h1>
        <p className={styles.subtitle}>Прежние устройства разлогинены, на этом вы уже вошли.</p>
        <button type="button" className={styles.primary} onClick={onDone}>В Кличат</button>
      </div>
    )
  }
  return (
    <form className={styles.form} onSubmit={(e) => void submit(e)} noValidate>
      <h1 className={styles.title}>Новый пароль</h1>
      <p className={styles.subtitle}>Задайте новый пароль — после смены все прежние устройства будут разлогинены.</p>
      <Field label="Новый пароль" hint="Не короче 8 символов" error={fields.new_password ?? fields.password} htmlFor="link-password">
        <input id="link-password" className={styles.input} type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required autoFocus />
      </Field>
      {error && !fields.new_password && !fields.password ? <p className={styles.error} role="alert">{error}</p> : null}
      <button type="submit" className={styles.primary} disabled={busy || password.length < 8}>
        {busy ? 'Сохраняем…' : 'Сменить пароль и войти'}
      </button>
      <p className={styles.footer}>
        <button type="button" className={styles.link} onClick={onDone}>Вернуться ко входу</button>
      </p>
    </form>
  )
}
